import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { ConnectionStatus } from '../../models/integration/connection-status.enum';
import { IIntegrationConnection } from '../../models/integration/integration-connection.model';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { IntegrationProvider } from '../../provider/integration.provider';

/**
 * View shown in the modal. Mirrors the D365 BC manage modal's `ManageView`, minus the
 * `detail` view — the flat connection list already IS the detail view here.
 */
export type SharepointManageView = 'list' | 'confirm_disconnect';

/**
 * REV-21860 — manage modal for the SharePoint card in Organization → Settings →
 * Integrations.
 *
 * Deliberately much simpler than the D365 BC / Abacus / Topal manage modals: those exist
 * mainly to maintain per-company `ConnectionMapping`s, and SharePoint has none — it is a
 * document source, so what a closing binds are FOLDERS, and those are managed on the
 * closing itself (`/projects/:id/sharepoint-folders`), not here. This modal therefore only
 * does what the org level actually owns: show the tenant connections and let an admin
 * disconnect one or add another.
 */
@Component({
    selector: 'aumico-sharepoint-manage-modal',
    templateUrl: './sharepoint-manage-modal.component.html',
    styleUrls: ['./sharepoint-manage-modal.component.scss'],
})
export class SharepointManageModalComponent {
    /**
     * Present for symmetry with the other manage modals (the overview passes the
     * just-created connection id on `connectionJustCreated`). There is no drill-in detail
     * view to open here — the flat connection list already IS the detail view — so it is
     * used only to highlight the row the user just created.
     */
    @Input() public initialConnectionId: string | null = null;

    @Output() public closed = new EventEmitter<void>();
    @Output() public requestAddConnection = new EventEmitter<void>();

    public readonly connections$: Observable<IIntegrationConnection[]> =
        this.integrationProvider.getConnectionsByVendor$(IntegrationVendor.SHAREPOINT);

    public readonly ConnectionStatus = ConnectionStatus;

    public view: SharepointManageView = 'list';

    /** Held between "Verbindung trennen" and the confirm/cancel decision. */
    public disconnectCandidate: IIntegrationConnection | null = null;

    public constructor(private readonly integrationProvider: IntegrationProvider) {}

    // ── Disconnect (destructive, two-stage confirm) ────────────────────────
    //
    // Two-stage on purpose, matching D365BcManageModalComponent. Disconnecting is a
    // DELETE with no undo, and for SharePoint the blast radius reaches past this org
    // connection: every closing whose `SelectedClosingFolder` bindings point at this
    // `connectionId` loses the source its picker tokens are minted from, so document
    // sync breaks across all of them. This modal also auto-opens straight after a
    // successful connect (`connectionJustCreated`), which puts the button under a
    // cursor that just clicked — exactly when a one-click delete is least wanted.

    public onRequestDisconnect(connection: IIntegrationConnection): void {
        this.disconnectCandidate = connection;
        this.view = 'confirm_disconnect';
    }

    public onCancelDisconnect(): void {
        this.disconnectCandidate = null;
        this.view = 'list';
    }

    public onConfirmDisconnect(): void {
        if (this.disconnectCandidate == null) return;
        this.integrationProvider.disconnect(this.disconnectCandidate.id);
        this.disconnectCandidate = null;
        this.view = 'list';
    }

    public onAddConnection(): void {
        this.requestAddConnection.emit();
    }

    public onClose(): void {
        this.closed.emit();
    }

    public trackByConnection(_: number, connection: IIntegrationConnection): string {
        return connection.id;
    }
}
